// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {ChatDetailsPanel} from '@app/features/tasks/components/ChatDetailsPanel';
import styles from '@app/features/tasks/components/ChatDetailsPanel.module.css';
import ChatDetailsPanelState from '@app/features/tasks/state/ChatDetailsPanel';
import {
	DETAILS_DESCRIPTOR,
	HIDE_DETAILS_DESCRIPTOR,
	SHOW_DETAILS_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

/**
 * Opens this chat's to-dos and files. On a computer it toggles the side pane;
 * on a phone it opens them in a sheet from the bottom, as in the design.
 */
export const ChatDetailsButton = observer(({channel}: {channel?: Channel}) => {
	const {i18n} = useLingui();
	if (MobileLayout.enabled) {
		return (
			<>
				<button
					type="button"
					className={styles.detailsToggle}
					aria-expanded={ChatDetailsPanelState.open}
					onClick={ChatDetailsPanelState.toggle}
					data-flx="tasks.chat-details-button.mobile-toggle"
				>
					{i18n._(DETAILS_DESCRIPTOR)}
				</button>
				{channel && (
					<BottomSheet
						isOpen={ChatDetailsPanelState.open}
						onClose={ChatDetailsPanelState.close}
						initialSnap={2}
						showCloseButton={false}
						disablePadding
						data-flx="tasks.chat-details-button.bottom-sheet"
					>
						<div className={styles.inSheet} data-flx="tasks.chat-details-button.in-sheet">
							<ChatDetailsPanel channel={channel} data-flx="tasks.chat-details-button.chat-details-panel" />
						</div>
					</BottomSheet>
				)}
			</>
		);
	}
	return (
		<button
			type="button"
			className={styles.detailsToggle}
			aria-pressed={ChatDetailsPanelState.open}
			onClick={ChatDetailsPanelState.toggle}
			data-flx="tasks.chat-details-button.toggle"
		>
			{i18n._(ChatDetailsPanelState.open ? HIDE_DETAILS_DESCRIPTOR : SHOW_DETAILS_DESCRIPTOR)}
		</button>
	);
});
